import { createSlice } from "@reduxjs/toolkit"
import onboardingAPI from "./onboardingAPI"
import { createAppAsyncThunk } from "app/hooks"
import {
  IOnboardingState,
  ISetGoalPayload,
  IUser,
  IUserResponse,
} from "./types"
import { formatBirthedOn, titleCase, is404Response } from "utils"
import { resetStoreAction } from "config"
import convertResponseToClientGoal from "utils/convertResponseToClientGoal"

const convertUserResponseToClientUser = (response: IUserResponse): IUser => {
  const {
    gender,
    birthedOn,
    showTutorial: isShowTutorial,
    paymentSupported,
  } = response

  const user: IUser = {
    gender: titleCase(gender),
    birthedOn: formatBirthedOn(birthedOn),
    isShowTutorial,
    country: response.country,
    paymentSupported,
  }

  return user
}

export const getUser = createAppAsyncThunk(
  "onboarding/getUser",
  async (isRevalidated: boolean, { rejectWithValue }) => {
    try {
      const response = await onboardingAPI.getUser()
      const user = convertUserResponseToClientUser(response)
      return {
        user,
        isRevalidated,
      }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getGoal = createAppAsyncThunk(
  "onboarding/getGoal",
  async (isRetried: boolean, { rejectWithValue, dispatch }) => {
    try {
      const response = await onboardingAPI.getGoal()
      return response ? convertResponseToClientGoal(response) : undefined
    } catch (err: any) {
      if (!isRetried && is404Response(err)) {
        setTimeout(() => {
          dispatch(getGoal(true))
        }, 1000)
      } else {
        return rejectWithValue({ err, isRetried: true })
      }
    }
  },
)

export const onboarding = createAppAsyncThunk(
  "onboarding/onboarding",
  async (payload: ISetGoalPayload, { rejectWithValue }) => {
    try {
      const response = await onboardingAPI.setGoal(payload)
      return convertResponseToClientGoal(response)
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const turnOffWeightChangeNotification = createAppAsyncThunk(
  "onboarding/turnOffWeightChangeNotification",
  async (_, { rejectWithValue }) => {
    try {
      await onboardingAPI.turnOfWeightChangedNotification()
      return {}
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IOnboardingState = {
  onboardingLoading: false,
  onboardingFailed: null,
  onboardingSuccess: null,

  getGoalLoading: false,
  goal: undefined,
  getGoalFailed: null,
  isNotHasGoalYet: false,

  getUserLoading: false,
  user: undefined,
  getUserFailed: null,

  isTutorialShowed: false,
  isRevalidatedShouldShowTutorial: false,
  tutorialBackPath: undefined,

  updatingUser: undefined,

  shouldShowWeightChangedNotification: false,
  turnOffWeightChangeNotificationFailed: undefined,
}

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    resetOnboardingState: (state) => {
      state.onboardingSuccess = undefined
      state.onboardingFailed = undefined
    },

    setIsTutorialShowed: (state, { payload }) => {
      state.isTutorialShowed = payload
    },

    setOnboardingGoal: (state, { payload }) => {
      state.goal = payload
    },

    setTutorialBackPath: (state, { payload }) => {
      state.tutorialBackPath = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(onboarding.pending, (state) => {
        state.onboardingLoading = true
      })
      .addCase(onboarding.fulfilled, (state, { payload }) => {
        state.onboardingLoading = false
        state.onboardingSuccess = payload
        state.goal = payload
      })
      .addCase(onboarding.rejected, (state, { payload }) => {
        state.onboardingLoading = false
        state.onboardingFailed = payload
      })

      .addCase(getGoal.pending, (state) => {
        state.getGoalLoading = true
      })
      .addCase(getGoal.fulfilled, (state, { payload }) => {
        if (payload === undefined) {
          return
        }

        state.getGoalLoading = false
        state.goal = payload
        state.shouldShowWeightChangedNotification =
          payload.shouldShowWeightChangedNotification
      })
      .addCase(getGoal.rejected, (state, { payload }: any) => {
        if (!payload.isRetried) {
          return
        }

        state.getGoalLoading = false
        state.getGoalFailed = payload.err
        state.goal = undefined
        state.isNotHasGoalYet = is404Response(payload.err)
      })

      .addCase(getUser.pending, (state) => {
        state.getUserLoading = true
        state.getGoalFailed = undefined
      })
      .addCase(getUser.fulfilled, (state, { payload }) => {
        state.getUserLoading = false
        state.user = payload.user

        if (payload.isRevalidated) {
          state.isRevalidatedShouldShowTutorial = true
        }
      })
      .addCase(getUser.rejected, (state, { payload }) => {
        state.getUserLoading = false
        state.getUserFailed = is404Response(payload) ? undefined : payload
        state.user = {
          gender: "",
          birthedOn: "",
          isShowTutorial: true,
          country: "",
          paymentSupported: false,
        }
      })

      .addCase(turnOffWeightChangeNotification.pending, (state) => {
        state.shouldShowWeightChangedNotification = false
        state.turnOffWeightChangeNotificationFailed = undefined
      })
      .addCase(
        turnOffWeightChangeNotification.rejected,
        (state, { payload }) => {
          state.shouldShowWeightChangedNotification = true
          state.turnOffWeightChangeNotificationFailed = payload
        },
      )

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const {
  resetOnboardingState,
  setIsTutorialShowed,
  setOnboardingGoal,
  setTutorialBackPath,
} = onboardingSlice.actions

export default onboardingSlice.reducer
